import { useState } from 'react';
import type { FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Archive } from 'lucide-react';
import { Link } from 'react-router-dom';
import { CONTAINER_KIND_LABELS, CONTAINER_KIND_PLURALS, stuckProjects } from '@cerebro-diem/core';
import type { ContainerKind, ProjectStatus, Task } from '@cerebro-diem/core';
import { supabase } from '../../services/supabase';
import { errorMessage } from '../../services/errors';
import { LIST_LIMIT, queryKeys, reachedLimit } from '../../services/queries';
import { CONTAINER_TABLES, containerPath, createContainer } from '../../services/containers';
import { chipClass } from '../../components/ContainerChip';
import { inputClass, primaryButton, tapLink } from '../../components/styles';
import { CONTAINER_ICONS, PROJECT_STATUS_LABELS } from './labels';

interface ContainerListRow {
  id: string;
  name: string;
  description?: string | null;
  status?: ProjectStatus;
  archived_at: string | null;
}

type TaskCount = Pick<Task, 'project_id' | 'area_id' | 'status' | 'gtd_status'>;
type NotePlace = { project_id: string | null; area_id: string | null; resource_id: string | null };

const plural = (n: number, one: string) => `${n} ${one}${n === 1 ? '' : 's'}`;

/** The active Projects, Areas or Resources, with what is in each, and a way to add one. */
export default function ContainerListPage({ kind }: { kind: ContainerKind }) {
  const table = CONTAINER_TABLES[kind];
  const Icon = CONTAINER_ICONS[kind];
  const queryClient = useQueryClient();
  const [name, setName] = useState('');

  const listQuery = useQuery({
    queryKey: queryKeys.containerList(table),
    queryFn: async () => {
      let query = supabase.from(table).select('*').is('archived_at', null).limit(LIST_LIMIT);
      query = kind === 'project' ? query.order('updated_at', { ascending: false }) : query.order('sort_order').order('name');
      const { data, error } = await query;
      if (error) throw error;
      return (data ?? []) as ContainerListRow[];
    },
  });
  const tasksQuery = useQuery({
    queryKey: [...queryKeys.tasks, 'open-by-container'],
    enabled: kind !== 'resource',
    queryFn: async () => {
      const { data, error } = await supabase.from('tasks').select('project_id, area_id, status, gtd_status').neq('status', 'done').limit(LIST_LIMIT);
      if (error) throw error;
      return (data ?? []) as TaskCount[];
    },
  });
  const notesQuery = useQuery({
    queryKey: [...queryKeys.notes, 'by-container'],
    queryFn: async () => {
      const { data, error } = await supabase.from('notes').select('project_id, area_id, resource_id').limit(LIST_LIMIT);
      if (error) throw error;
      return (data ?? []) as NotePlace[];
    },
  });
  const createMutation = useMutation({
    mutationFn: () => createContainer(kind, name),
    onSuccess: async () => {
      setName('');
      await queryClient.invalidateQueries({ queryKey: queryKeys.containers });
      await queryClient.invalidateQueries({ queryKey: queryKeys.searches });
    },
  });

  const rows = listQuery.data ?? [];
  const column = kind === 'project' ? 'project_id' : kind === 'area' ? 'area_id' : 'resource_id';
  const openTasks = (id: string) => (tasksQuery.data ?? []).filter((t) => t[column as 'project_id' | 'area_id'] === id).length;
  const notes = (id: string) => (notesQuery.data ?? []).filter((n) => n[column] === id).length;
  const stuck =
    kind === 'project' && tasksQuery.isSuccess
      ? stuckProjects(rows.map((r) => ({ id: r.id, status: r.status ?? 'active', archived_at: r.archived_at })), tasksQuery.data)
      : new Set<string>();

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (name.trim()) createMutation.mutate();
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex flex-wrap items-center gap-4">
        <Icon className="w-8 h-8 text-primary" />
        <h1 className="text-2xl font-bold">{CONTAINER_KIND_PLURALS[kind]}</h1>
        <Link to="/archive" className={`ml-auto ${tapLink} gap-1 text-sm text-primary hover:underline`}>
          <Archive className="w-4 h-4" /> Archive
        </Link>
      </div>

      <form onSubmit={submit} className="flex gap-2">
        <input
          className={`${inputClass} min-w-0 flex-1`}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={`New ${CONTAINER_KIND_LABELS[kind].toLowerCase()}`}
          aria-label={`Name of a new ${CONTAINER_KIND_LABELS[kind].toLowerCase()}`}
        />
        <button type="submit" disabled={!name.trim() || createMutation.isPending} className={primaryButton}>
          Add
        </button>
      </form>
      {createMutation.isError && <p className="text-sm text-error">Not added: {errorMessage(createMutation.error)}</p>}

      {listQuery.isError && <p className="text-sm text-error">Could not {listQuery.data === undefined ? 'load' : 'refresh'} {CONTAINER_KIND_PLURALS[kind].toLowerCase()}: {errorMessage(listQuery.error)}</p>}
      {listQuery.isError && listQuery.data === undefined ? null : listQuery.isPending ? (
        <p className="text-sm text-on-surface-variant">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-center py-12 text-on-surface-variant">No active {CONTAINER_KIND_PLURALS[kind].toLowerCase()}.</p>
      ) : (
        <ul className="space-y-3">
          {rows.map((row) => (
            <li key={row.id}>
              <Link
                to={containerPath(kind, row.id)}
                className="block bg-surface rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="min-w-0 font-semibold break-words">{row.name}</h2>
                  {row.status && row.status !== 'active' && (
                    <span className={`${chipClass} bg-surface-variant text-on-surface-variant`}>{PROJECT_STATUS_LABELS[row.status]}</span>
                  )}
                  {stuck.has(row.id) && (
                    <span className={`${chipClass} inline-flex items-center gap-1 bg-error/10 text-error`}>
                      <AlertTriangle className="w-3 h-3" /> No next action
                    </span>
                  )}
                </div>
                {row.description && <p className="text-sm text-on-surface-variant mt-1">{row.description}</p>}
                <p className="text-xs text-on-surface-variant mt-2">
                  {kind !== 'resource' && tasksQuery.isSuccess && `${plural(openTasks(row.id), 'open task')} · `}
                  {notesQuery.isSuccess && plural(notes(row.id), 'note')}
                </p>
              </Link>
            </li>
          ))}
        </ul>
      )}
      {(reachedLimit(listQuery.data) || reachedLimit(tasksQuery.data) || reachedLimit(notesQuery.data)) && (
        <p role="status" className="text-sm text-tertiary">
          Only the first {LIST_LIMIT.toLocaleString()} rows were read, so this list or its counts may be incomplete.
        </p>
      )}
      {(tasksQuery.isError || notesQuery.isError) && (
        <p className="text-sm text-error">
          Could not load what is in each {CONTAINER_KIND_LABELS[kind].toLowerCase()}:{' '}
          {errorMessage(tasksQuery.error ?? notesQuery.error)}
        </p>
      )}
    </div>
  );
}
